import { RoomRefDTO } from '../../../../dto/location/room.dto';
import { EventOrganizationResourceDTO } from '../../../../dto/event/event.dto';

export type AdditionalRoomFormField =
  | 'primary_extra_room'
  | 'sub_extra_room'
  | 'exception_extra_room'
  | 'extra_room';

const ADDITIONAL_ROOM_GUID_KEYS = [
  'primary_extra_room',
  'sub_extra_room',
  'exception_extra_room',
  'extra_room',
  'guid',
  'room_guid',
] as const;

function readAdditionalRoomGuidFromEntry(entry: unknown): string | null {
  if (typeof entry === 'string' && entry.trim()) {
    return entry.trim();
  }
  if (!entry || typeof entry !== 'object') {
    return null;
  }
  const row = entry as Record<string, unknown>;
  for (const key of ADDITIONAL_ROOM_GUID_KEYS) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return null;
}

/** Map paneldynamic / form values to API additional_rooms payload. */
export function mapAdditionalRoomsToApi(
  raw: unknown,
  options?: { excludeRoomGuid?: string | null }
): RoomRefDTO[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const exclude = typeof options?.excludeRoomGuid === 'string' ? options.excludeRoomGuid.trim() : '';
  const guids: string[] = [];
  for (const entry of raw) {
    const guid = readAdditionalRoomGuidFromEntry(entry);
    if (!guid) {
      continue;
    }
    if (exclude && guid === exclude) {
      continue;
    }
    guids.push(guid);
  }
  return [...new Set(guids)].map((guid) => ({ guid }));
}

/** Map tagbox / checkbox values to organization resource ids for API. */
export function mapOrganizationResourceIdsToApi(raw: unknown): EventOrganizationResourceDTO[] {
  if (raw == null) {
    return [];
  }
  const ids: number[] = [];
  const list = Array.isArray(raw) ? raw : [raw];
  for (const entry of list) {
    if (typeof entry === 'number' && entry > 0) {
      ids.push(entry);
      continue;
    }
    if (typeof entry === 'string' && entry.trim()) {
      const n = parseInt(entry, 10);
      if (Number.isFinite(n) && n > 0) {
        ids.push(n);
      }
      continue;
    }
    if (entry && typeof entry === 'object') {
      const id = parseInt(String((entry as Record<string, unknown>)['id'] ?? ''), 10);
      if (Number.isFinite(id) && id > 0) {
        ids.push(id);
      }
    }
  }
  return [...new Set(ids)].map((id) => ({ id }));
}

export function mapAdditionalRoomsToForm(
  rooms?: Array<{ guid?: string } | null> | null,
  fieldName: AdditionalRoomFormField = 'primary_extra_room'
): Array<Record<string, string>> {
  if (!Array.isArray(rooms)) {
    return [];
  }
  return rooms
    .map((room) => room?.guid)
    .filter((guid): guid is string => typeof guid === 'string' && guid.trim() !== '')
    .map((guid) => ({ [fieldName]: guid }));
}

export function mapOrganizationResourceIdsToForm(
  resources?: Array<{ id?: number } | null> | null
): number[] {
  if (!Array.isArray(resources)) {
    return [];
  }
  return resources
    .map((r) => r?.id)
    .filter((id): id is number => typeof id === 'number' && id > 0);
}

function sanitizeAdditionalRoomPanel(
  rows: unknown,
  fieldName: AdditionalRoomFormField,
  excludeRoomGuid?: string | null
): Array<Record<string, string>> {
  const mapped = mapAdditionalRoomsToApi(rows, { excludeRoomGuid });
  return mapAdditionalRoomsToForm(mapped, fieldName);
}

/** Remove primary-room duplicates from extra-room paneldynamic values before submit. */
export function sanitizeAdditionalRoomsFormData(data: Record<string, unknown>): void {
  const primaryRoom =
    typeof data['room'] === 'string'
      ? data['room'].trim()
      : typeof data['predefined_room_guid'] === 'string'
        ? data['predefined_room_guid'].trim()
        : '';

  if (Array.isArray(data['additional_rooms'])) {
    data['additional_rooms'] = sanitizeAdditionalRoomPanel(
      data['additional_rooms'],
      'primary_extra_room',
      primaryRoom || null
    );
  }

  const additionalDates = data['additional_dates'];
  if (Array.isArray(additionalDates)) {
    for (const row of additionalDates) {
      if (!row || typeof row !== 'object') {
        continue;
      }
      const entry = row as Record<string, unknown>;
      const subRoom =
        typeof entry['sub_event_room'] === 'string' ? entry['sub_event_room'].trim() : primaryRoom;
      if (Array.isArray(entry['sub_event_additional_rooms'])) {
        entry['sub_event_additional_rooms'] = sanitizeAdditionalRoomPanel(
          entry['sub_event_additional_rooms'],
          'sub_extra_room',
          subRoom || null
        );
      }
    }
  }

  const seriesExceptions = data['series_exceptions'];
  if (Array.isArray(seriesExceptions)) {
    for (const row of seriesExceptions) {
      if (!row || typeof row !== 'object') {
        continue;
      }
      const entry = row as Record<string, unknown>;
      const exceptionRoom =
        typeof entry['exception_room'] === 'string' ? entry['exception_room'].trim() : '';
      if (Array.isArray(entry['exception_additional_rooms'])) {
        entry['exception_additional_rooms'] = sanitizeAdditionalRoomPanel(
          entry['exception_additional_rooms'],
          'exception_extra_room',
          exceptionRoom || null
        );
      }
    }
  }
}
